import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowUpRight,
    ShieldCheck,
    Landmark,
    Users,
    CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { createLead } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { delay: i * 0.06, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
    }),
};

export default function Hero() {
    const { settings } = useSettings();
    const navigate = useNavigate();

    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
    });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone) {
            toast.error("Please add your name, phone and email.");
            return;
        }
        setLoading(true);
        try {
            await createLead({
                ...form,
                service: "Mortgage enquiry",
                source: "hero_form",
                message: "Speak to a mortgage adviser — hero quote request",
            });
            toast.success("Thanks — an adviser will call you back today.");
            navigate("/thank-you");
        } catch {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const set = (k, v) => setForm({ ...form, [k]: v });

    return (
        <section data-testid="hero-section" className="relative overflow-hidden bg-brand-bg">
            {/* Hero background video — couple in their new home */}
            <video
                aria-hidden="true"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                poster="https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?auto=format&fit=crop&w=2400&q=90"
                className="absolute inset-0 w-full h-full object-cover"
            >
                {/* Primary: couple unpacking / settling into a new home */}
                <source
                    src="https://videos.pexels.com/video-files/4625671/4625671-hd_1920_1080_25fps.mp4"
                    type="video/mp4"
                />
                {/* Fallback: family in a bright modern home */}
                <source
                    src="https://videos.pexels.com/video-files/8927649/8927649-hd_1920_1080_25fps.mp4"
                    type="video/mp4"
                />
                {/* Fallback: bright modern house tour (last-resort) */}
                <source
                    src="https://videos.pexels.com/video-files/7578541/7578541-uhd_2560_1440_30fps.mp4"
                    type="video/mp4"
                />
            </video>
            {/* Light overlay so video shines through */}
            <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{ backgroundColor: "rgba(255, 247, 237, 0.30)" }}
            />
            {/* Left-side gradient to keep headline readable */}
            <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                    backgroundImage:
                        "linear-gradient(90deg, rgba(255,247,237,0.88) 0%, rgba(255,247,237,0.55) 40%, rgba(255,247,237,0.20) 70%, rgba(255,247,237,0.10) 100%)",
                }}
            />
            {/* Warm orange accent gradients for depth */}
            <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                    backgroundImage:
                        "radial-gradient(1200px 600px at 80% 20%, rgba(249,115,22,0.18), transparent 60%), radial-gradient(900px 500px at 10% 80%, rgba(194,65,12,0.14), transparent 65%)",
                }}
            />
            <div aria-hidden className="absolute inset-0 grain opacity-30" />

            <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-16 pt-16 md:pt-20 pb-10 md:pb-16">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                    {/* Left — headline + CTA */}
                    <div className="lg:col-span-7">
                        <motion.div
                            initial="hidden" animate="visible" variants={fadeUp} custom={0}
                            className="flex items-center gap-3 mb-6"
                        >
                            <span className="h-px w-10 bg-brand-accent" />
                            <span className="text-xs uppercase tracking-[0.24em] text-brand-accent font-bold">
                                Mortgage advisor — UK
                            </span>
                        </motion.div>

                        <motion.h1
                            initial="hidden" animate="visible" variants={fadeUp} custom={1}
                            data-testid="hero-title"
                            className="font-serif-editorial text-[44px] sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-brand-primary"
                        >
                            Speak to a mortgage
                            <br />
                            <em className="italic font-light text-brand-accent">
                                adviser today.
                            </em>
                        </motion.h1>

                        <motion.p
                            initial="hidden" animate="visible" variants={fadeUp} custom={2}
                            className="mt-6 text-lg md:text-xl text-brand-muted leading-relaxed max-w-2xl"
                        >
                            Honest mortgage and protection advice for homeowners,
                            landlords and business owners across the UK — with
                            access to a large panel of UK lenders through HL
                            Partnership.
                        </motion.p>

                        {/* Above-the-fold trust bullets — exactly 3, high-intent */}
                        <motion.ul
                            initial="hidden" animate="visible" variants={fadeUp} custom={3}
                            className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-xl"
                        >
                            {[
                                "No-obligation consultation",
                                "Compare 90+ lenders",
                                "Same-day callback",
                            ].map((t) => (
                                <li
                                    key={t}
                                    className="flex items-center gap-2.5 text-sm text-brand-primary font-semibold"
                                >
                                    <CheckCircle2 size={16} className="text-brand-accent shrink-0" />
                                    {t}
                                </li>
                            ))}
                        </motion.ul>

                        {/* Trust signals strip */}
                        <motion.div
                            initial="hidden" animate="visible" variants={fadeUp} custom={4}
                            data-testid="hero-trust-strip"
                            className="mt-10 grid grid-cols-3 gap-6 max-w-xl pt-6 border-t border-brand-border"
                        >
                            <TrustStat
                                icon={ShieldCheck}
                                headline="Authorised adviser"
                                sub="Appointed rep of HL Partnership"
                            />
                            <TrustStat
                                icon={Landmark}
                                headline="90+ lenders"
                                sub="High-street & specialist"
                            />
                            <TrustStat
                                icon={Users}
                                headline="UK-wide"
                                sub="One adviser, end-to-end"
                            />
                        </motion.div>
                    </div>

                    {/* Right — lead capture form */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:col-span-5"
                    >
                        <div className="bg-brand-surface border border-brand-border shadow-[0_30px_60px_-20px_rgba(30,79,138,0.22)] p-8 md:p-10">
                            <div className="flex items-center gap-3 mb-4">
                                <span className="h-2.5 w-2.5 bg-brand-accent rounded-full" />
                                <span className="text-[11px] uppercase tracking-[0.22em] text-brand-accent font-bold">
                                    Get a free quote
                                </span>
                            </div>
                            <h2
                                data-testid="hero-form-title"
                                className="font-serif-editorial text-3xl md:text-4xl text-brand-primary leading-[1.1]"
                            >
                                Speak to a mortgage
                                <br />
                                <em className="italic font-light text-brand-accent">
                                    adviser today.
                                </em>
                            </h2>
                            <p className="mt-3 text-sm text-brand-muted">
                                No-obligation. Takes under 60 seconds. Initial
                                consultations are completely free of charge —
                                our broker fee (typically £449–£799) only
                                becomes payable if we proceed to a full
                                application.
                            </p>

                            <form
                                onSubmit={submit}
                                data-testid="hero-lead-form"
                                className="mt-6 space-y-4"
                            >
                                <HeroField
                                    label="Name"
                                    value={form.name}
                                    onChange={(v) => set("name", v)}
                                    testid="hero-field-name"
                                    autoComplete="name"
                                    required
                                />
                                <HeroField
                                    label="Phone"
                                    type="tel"
                                    value={form.phone}
                                    onChange={(v) => set("phone", v)}
                                    testid="hero-field-phone"
                                    autoComplete="tel"
                                    required
                                />
                                <HeroField
                                    label="Email"
                                    type="email"
                                    value={form.email}
                                    onChange={(v) => set("email", v)}
                                    testid="hero-field-email"
                                    autoComplete="email"
                                    required
                                />

                                <button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="hero-submit"
                                    className="group mt-2 w-full inline-flex items-center justify-center gap-2 bg-brand-accent text-white px-6 py-4 rounded-full text-sm font-bold tracking-wide hover:bg-brand-accent/90 transition-colors disabled:opacity-60"
                                >
                                    {loading ? "Sending…" : "Get My Free Quote"}
                                    <ArrowUpRight
                                        size={15}
                                        className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                                    />
                                </button>

                                <p className="text-[11px] text-brand-muted leading-relaxed pt-1">
                                    By submitting your details, you confirm you have
                                    read and understood our{" "}
                                    <a
                                        href="/privacy"
                                        className="underline text-brand-primary hover:text-brand-accent"
                                    >
                                        Privacy Policy
                                    </a>
                                    . An adviser will contact you about your enquiry.
                                </p>
                                <p
                                    data-testid="hero-risk-warning"
                                    className="text-[11px] text-brand-primary font-bold leading-snug pt-2 mt-2 border-t border-brand-border/60"
                                >
                                    Your home may be repossessed if you do not keep up
                                    repayments on your mortgage.
                                </p>
                            </form>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

function HeroField({ label, value, onChange, type = "text", testid, placeholder, autoComplete, required }) {
    return (
        <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-brand-muted font-bold">
                {label}{required && <span className="text-brand-accent"> *</span>}
            </label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                placeholder={placeholder}
                autoComplete={autoComplete}
                required={required}
                className="mt-1.5 w-full bg-transparent border-b border-brand-border py-3 text-base text-brand-primary placeholder-brand-muted focus:border-brand-primary outline-none transition-colors"
            />
        </div>
    );
}

function TrustStat({ icon: Icon, headline, sub }) {
    return (
        <div className="flex items-start gap-2.5">
            <Icon size={18} className="text-brand-primary mt-0.5 shrink-0" />
            <div>
                <div className="text-sm font-bold text-brand-primary leading-tight">{headline}</div>
                <div className="text-xs text-brand-muted mt-0.5 leading-tight">{sub}</div>
            </div>
        </div>
    );
}
